import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { BellRing, MessageSquareText, Mail, Smartphone, RefreshCw, FlaskConical } from "lucide-react";
import { Spinner, EmptyState } from "../../components/common";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Linkify } from "../../components/Linkify";
import { api, fmtDate } from "../../lib/api";

const EVENT_LABELS = {
  demand_created: "New demand",
  demand_corrected: "Demand corrected",
  payment_received: "Payment received",
  partial_payment: "Partial payment",
  cheque_bounced: "Cheque bounced",
  payment_due_reminder: "Property tax due",
  pending_payment_notice: "Pending payment notice",
  final_notice: "Final notice",
  visit_update: "Visit update",
  citizen_portal_payment: "Online payment",
};
const CHANNEL_ICON = { sms: Smartphone, whatsapp: MessageSquareText, email: Mail };
const CHANNEL_LABEL = { sms: "SMS", whatsapp: "WhatsApp", email: "Email" };
const STATUS_VARIANT = { sent: "default", simulated: "secondary", failed: "destructive" };

export default function MyAlerts() {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [filterChannel, setFilterChannel] = useState("all");
  const [filterEvent, setFilterEvent] = useState("all");

  const load = useCallback(() => {
    const params = { limit: 100 };
    if (filterChannel !== "all") params.channel = filterChannel;
    if (filterEvent !== "all") params.event = filterEvent;
    api.get(`/t/${code}/citizen/my-alerts`, { params }).then((r) => setData(r.data)).catch(() => setData({ items: [], total: 0 }));
  }, [code, filterChannel, filterEvent]);
  useEffect(() => { load(); }, [load]);

  const hasSimulated = (data?.items || []).some((i) => i.status === "simulated");

  return (
    <div className="space-y-5" data-testid="my-alerts-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold font-heading flex items-center gap-2">
            <BellRing className="h-6 w-6" /> My Alerts
          </h1>
          <p className="text-muted-foreground text-sm">
            Every SMS, WhatsApp and Email notice sent to you about your properties
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filterChannel} onValueChange={(v) => v && setFilterChannel(v)}>
            <SelectTrigger className="w-32 h-9 text-xs" data-testid="alerts-channel-filter">
              <SelectValue>{filterChannel === "all" ? "All channels" : CHANNEL_LABEL[filterChannel]}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All channels</SelectItem>
              <SelectItem value="sms">SMS</SelectItem>
              <SelectItem value="whatsapp">WhatsApp</SelectItem>
              <SelectItem value="email">Email</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterEvent} onValueChange={(v) => v && setFilterEvent(v)}>
            <SelectTrigger className="w-40 h-9 text-xs" data-testid="alerts-event-filter">
              <SelectValue>{filterEvent === "all" ? "All notices" : EVENT_LABELS[filterEvent]}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All notices</SelectItem>
              {Object.keys(EVENT_LABELS).map((ev) => <SelectItem key={ev} value={ev}>{EVENT_LABELS[ev]}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" className="h-9 w-9" onClick={load} data-testid="alerts-refresh-button">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {hasSimulated && (
        <Card className="border-amber-300 bg-amber-50/60">
          <CardContent className="p-3 flex items-center gap-2">
            <FlaskConical className="h-4 w-4 text-amber-600 shrink-0" />
            <p className="text-xs text-amber-800">
              Alerts marked <span className="font-semibold">simulated</span> were prepared during the trial phase and were not delivered to your phone/email.
            </p>
          </CardContent>
        </Card>
      )}

      {!data ? <Spinner /> : data.items.length === 0 ? (
        <EmptyState title="No alerts yet"
          subtitle={data.linked_properties === 0
            ? "Link your property (My Properties → search by PIN) to start receiving alerts about demands and payments"
            : "You will see SMS, WhatsApp and Email notices here when new demands are generated or payments are received"} />
      ) : (
        <div className="space-y-2">
          {data.items.map((it) => {
            const Icon = CHANNEL_ICON[it.channel] || BellRing;
            return (
              <Card key={it.id} className="shadow-sm" data-testid="alert-item">
                <CardContent className="p-3.5 flex gap-3">
                  <div className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
                    <Icon className="h-4.5 w-4.5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-medium">{EVENT_LABELS[it.event] || it.event}</p>
                      <Badge variant="outline" className="text-[10px] uppercase">{CHANNEL_LABEL[it.channel] || it.channel}</Badge>
                      {it.pin && <span className="text-[11px] font-mono text-muted-foreground">{it.pin}</span>}
                      <Badge variant={STATUS_VARIANT[it.status] || "secondary"} className="text-[10px] ml-auto">{it.status}</Badge>
                    </div>
                    {it.subject && <p className="text-xs font-medium mt-1">{it.subject}</p>}
                    <p className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap line-clamp-3" title={it.message}><Linkify text={it.message} /></p>
                    <p className="text-[11px] text-muted-foreground mt-1.5">{fmtDate(it.created_at)} • to {it.to}</p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
          <p className="text-[11px] text-muted-foreground text-center pt-1">Showing {data.items.length} of {data.total} alerts</p>
        </div>
      )}
    </div>
  );
}
